import { paketDuzenlemesiCoz, paketDuzenlemesiniSinirla, paketFiyatiniDuzenle, type PaketDuzenlemeTaslagi, type PaketDuzenlemeTaslaklari } from './paket-duzenleme';
/**
 * Özel tatil paketi (hızlı teklif, CRM `/hizli-teklif`): tek ekran. Akış: talep (müşteri, odalar) -> konaklama bölümleri
 * (her biri tarih, oteller, "Fiyatları çek", sonuç tablosu) -> teklif özeti (sağ, yapışkan) -> WhatsApp taslağı -> sürümler.
 * Kaynak: `~/Downloads/hizli-teklif-taslak/SARTNAME-MASTER.md` (Alternatif A, 15. bölüm sapmalarıyla). Kurallar
 * `mantik.ts`te; burada ortak durum (müşteri, odalar, paket, çıktı, sürüm) var, bölüm başına durum `Bolum.tsx`te.
 * Akordiyon ve adım yok; formu otomatik daraltma yok; otomatik kaydırma yok.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../api';
import { OdaDagilimi } from '../OdaDagilimi';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { TarihAraligiSecici } from '../TarihSecici';
import { hizliTeklifApi, kayitHataCumlesi, sunucuCumlesi, ucYok } from './istemci';
import {
  AZAMI_BOLUM,
  DuyuruKuyrugu,
  KDV_VARSAYILAN,
  POLITIKA,
  YAT_BOYUTLARI,
  YAT_SAAT,
  belgeBasligi,
  bolumTarihHatalari,
  ciktiKapisi,
  destinasyonOner,
  eksikYaslar,
  gunSayisi,
  hizliYazimiCoz,
  hizliYazimiUygula,
  kalanMetni,
  kalemDegerlendir,
  kalemToplamlari,
  manuelAnahtar,
  yatSaatiCoz,
  yeniKalem,
  kombinasyonAdi,
  kritikVar,
  odaDuzenleri,
  odaToplamlari,
  ozetAraligi,
  paketDurumu,
  paketKullanilabilir,
  paraYaz,
  saatYaz,
  gunAyKisa,
  secenekToplami,
  tarihAraligiKisa,
  teklifSecimiKur,
  yeniOda,
  yeniOdaKimligi,
} from './mantik';
import { Bolum, type BolumRaporu } from './Bolum';
import { Ikon } from './Ikon';
import { taslagiGeriKur } from './taslak-geri-kur';
import { taslakOku, taslakYaz, type SaklananTaslak } from './taslak-depo';
import { musteriAdiBicimle } from './musteri-adi';
import { MesajBlogu } from './MesajBlogu';
import { MusteriAlani, type MusteriBilgisi } from './MusteriAlani';
import { PaketAlani, type AracGorunumu, type PaketGorunumu, type YatGorunumu } from './PaketAlani';
import { PlanAlani } from './PlanAlani';
import { SerbestKalemAlani, type KalemSecenegi } from './SerbestKalemAlani';
import { SurumSeridi } from './SurumSeridi';
import { TeklifOzeti, type OzetUyari, type PlanGorunumu } from './TeklifOzeti';
import type { AracSecimi, MesajTonu, OdaTaslagi, PaketFiyati, PaketKodu, SerbestKalem, SurumOzeti, TaslakBolum, TeklifSecimi, TeklifTaslagi, YatBoyutu } from './tipler';
import './hizliTeklif.css';

const PAKET_KODLARI: readonly PaketKodu[] = ['TRANSFER', 'DUBAI_TURU', 'ABU_DABI_TURU', 'SAFARI_TURU', 'HELIKOPTER_TURU', 'GOKYUZUNDE_YEMEK', 'OZEL_YAT_TURU'];
/** Uyruk seçeneği (ISO alpha-2): sık gelen müşteri uyrukları; listede olmayan kod (talepten gelen) ayrıca eklenir. */
const UYRUK_KODLARI: readonly string[] = ['TR', 'AZ', 'DE', 'GB', 'RU', 'US', 'FR', 'NL', 'IT', 'ES', 'KZ', 'UZ', 'GE', 'SA', 'AE', 'IQ', 'IR', 'KG', 'TM', 'UA', 'BG', 'RO', 'KW', 'QA', 'OM', 'BH', 'EG', 'LB', 'JO'];
const bolgeAdi = (k: string): string => { try { return new Intl.DisplayNames(['tr'], { type: 'region' }).of(k) ?? k; } catch { return k; } };

interface BolumTanimi {
  readonly anahtar: string;
  readonly giris: string;
  readonly cikis: string;
}

const kanonikHizli = (odalar: readonly OdaTaslagi[]): string => `${odalar.length} oda: ${odalar.map((o) => (o.cocukYaslari.length > 0 ? `${o.yetiskin}+${o.cocukYaslari.length}çocuk` : String(o.yetiskin))).join(', ')}`;

function mesajImzasi(gruplar: readonly { readonly secenekler: readonly { readonly otelAdi: string; readonly secenekMinor: number; readonly odalar: readonly { readonly secenekNo: string }[] }[] }[]): string {
  return JSON.stringify(gruplar.map((g) => g.secenekler.map((o) => [o.otelAdi, o.secenekMinor, o.odalar.map((x) => x.secenekNo)])));
}

/** Serbest kalemler, KDV notu ve yat seçimi mesajdaki tutarları ve cümleleri değiştirir: değişince mesaj eskimiş sayılır. */
function secimImzasi(g: TeklifSecimi): string {
  return `${mesajImzasi(g.gruplar)}|${g.aracSecimi ?? ''}|${JSON.stringify([g.serbestKalemler ?? [], g.kdvNotu ?? '', g.yat ?? null, g.safariBebekKoltukSayisi ?? 0])}`;
}

export function HizliTeklif({ musteriEkleyebilir = false }: { musteriEkleyebilir?: boolean }) {
  const kimlik = useId();
  // ---- müşteri ----
  const [musteriId, setMusteriId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('musteri'));
  const [musteri, setMusteri] = useState<MusteriBilgisi | null>(null);
  const [musteriYuk, setMusteriYuk] = useState(false);
  const [musteriHata, setMusteriHata] = useState<string | null>(null);
  // ---- talep: odalar bölümler arasında ortaktır ----
  const [odalar, setOdalar] = useState<OdaTaslagi[]>(() => [yeniOda(2)]);
  const [hizli, setHizli] = useState('');
  const [hizliHata, setHizliHata] = useState<string | null>(null);
  // ---- konaklama bölümleri ----
  const bolumSayaci = useRef(1);
  const [bolumler, setBolumler] = useState<readonly BolumTanimi[]>([{ anahtar: 'b1', giris: '', cikis: '' }]);
  const [aktifAnahtar, setAktifAnahtar] = useState('b1');
  const [raporlar, setRaporlar] = useState<Readonly<Record<string, BolumRaporu>>>({});
  // ---- paketler ----
  const [paketAcik, setPaketAcik] = useState<readonly PaketKodu[]>([]);
  const [paketVeri, setPaketVeri] = useState<{ readonly imza: string; readonly fiyatlar: readonly PaketFiyati[] } | null>(null);
  const [paketDuzenleme, setPaketDuzenleme] = useState<{ readonly imza: string; readonly degerler: PaketDuzenlemeTaslaklari } | null>(null);
  const [paketNotlari, setPaketNotlari] = useState<Readonly<Partial<Record<PaketKodu, string>>>>({});
  const [teklifNotuMetni, setTeklifNotuMetni] = useState('');
  const [aracSecimi, setAracSecimi] = useState<AracSecimi | null>(null);
  const [kalemler, setKalemler] = useState<readonly SerbestKalem[]>([]);
  const [kdvNotu, setKdvNotu] = useState(KDV_VARSAYILAN);
  const [planlamaMetni, setPlanlamaMetni] = useState('');
  const [isleyisMetni, setIsleyisMetni] = useState('');
  const [safariKoltuk, setSafariKoltuk] = useState(0);
  const [yatSaat, setYatSaat] = useState(String(YAT_SAAT.varsayilan));
  const [yatBoyut, setYatBoyut] = useState<YatBoyutu>('YAT_50FT');
  const [talepUyruk, setTalepUyruk] = useState<string | null>(null);
  const [uyrukSecimi, setUyrukSecimi] = useState<string | null>(null);
  const [destinasyonElle, setDestinasyonElle] = useState<string | null>(null);
  // ---- çıktı ----
  const [mesajMetni, setMesajMetni] = useState('');
  const [mesajKaynagi, setMesajKaynagi] = useState('');
  const [ton, setTon] = useState<MesajTonu>('SICAK');
  const [mesgul, setMesgul] = useState<'pdf' | 'mesaj' | null>(null);
  const [pdfDili, setPdfDili] = useState<'tr' | 'en'>('tr');
  const [onayBekliyor, setOnayBekliyor] = useState<'pdf' | 'mesaj' | null>(null);
  const [cikti, setCikti] = useState<string | null>(null);
  // ---- sürümler ----
  const [surumler, setSurumler] = useState<readonly SurumOzeti[]>([]);
  const [surumHata, setSurumHata] = useState<string | null>(null);
  const [salt, setSalt] = useState<{ readonly no: number; readonly taslak: TeklifTaslagi } | null>(null);
  // ---- duyuru ----
  const [canli, setCanli] = useState('');
  const kuyruk = useRef(new DuyuruKuyrugu());

  const duyur = useCallback((metin: string) => kuyruk.current.ekle(metin), []);
  // Tek canlı bölge: en çok 5 sn'de bir yazılır (şartname 7, 11).
  useEffect(() => {
    const t = window.setInterval(() => {
      const m = kuyruk.current.isle(Date.now());
      if (m !== null) setCanli(m);
    }, 500);
    return () => window.clearInterval(t);
  }, []);

  // ---- müşteri yükleme ----
  useEffect(() => {
    if (musteriId === null) { setMusteri(null); return undefined; }
    const c = new AbortController();
    setMusteriYuk(true); setMusteriHata(null);
    Promise.all([api.customer(musteriId), api.customerChannels(musteriId).catch(() => null)]).then(([m, kanallar]) => {
      if (c.signal.aborted) return;
      setMusteri({
        id: m.id,
        ad: musteriAdiBicimle(m.displayName),
        uyruk: m.countryCode !== null && /^[A-Za-z]{2}$/.test(m.countryCode) ? m.countryCode.toUpperCase() : null,
        whatsapp: kanallar?.items.some((k) => k.kind === 'WHATSAPP' && k.state === 'ACTIVE') ?? false,
      });
    }).catch(() => { if (!c.signal.aborted) { setMusteri(null); setMusteriHata(M.musteriYuklenemedi); } })
      .finally(() => { if (!c.signal.aborted) setMusteriYuk(false); });
    return () => c.abort();
  }, [musteriId]);

  // Varsayılan uyruk: müşterinin talebindeki uyruk (müşteri kaydında uyruk alanı yok). Alınamazsa TR varsayılır.
  useEffect(() => {
    setTalepUyruk(null); setUyrukSecimi(null);
    if (musteriId === null) return undefined;
    const c = new AbortController();
    hizliTeklifApi.musteriUyruk(musteriId, c.signal).then((v) => { if (!c.signal.aborted) setTalepUyruk(v.uyruk !== null && /^[A-Z]{2}$/.test(v.uyruk) ? v.uyruk : null); }).catch(() => undefined);
    return () => c.abort();
  }, [musteriId]);

  const musteriSec = useCallback((id: string | null) => {
    setMusteriId(id);
    const u = new URL(window.location.href);
    if (id === null) u.searchParams.delete('musteri'); else u.searchParams.set('musteri', id);
    window.history.replaceState(null, '', `${u.pathname}${u.search}`);
  }, []);

  // ---- türetilmiş talep ----
  // Uyruk: satışçının seçimi, yoksa müşterinin talebindeki uyruk, yoksa TR. "Seçin" (boş) seçilirse fiyat çekilmez.
  const uyruk = salt?.taslak.talep.uyruk ?? uyrukSecimi ?? talepUyruk ?? 'TR';
  const eksikler = useMemo(() => eksikYaslar(odalar), [odalar]);
  const yasTamam = eksikler.length === 0;
  const toplamlar = odaToplamlari(odalar);
  const saltOkunur = salt !== null;

  // Görünen bölümler: canlı ya da salt okunur sürümün bölümleri. Salt bölümlerin anahtarı `surum-` ile öne eklenir.
  const gorunen = useMemo(() => (salt === null
    ? bolumler.map((b) => ({ ...b, salt: null as TaslakBolum | null }))
    : salt.taslak.bolumler.map((b) => ({ anahtar: `surum-${b.anahtar}`, giris: b.girisTarihi, cikis: b.cikisTarihi, salt: b as TaslakBolum | null }))), [salt, bolumler]);
  const gorunenOdalar = useMemo<OdaTaslagi[]>(() => (salt === null ? odalar : salt.taslak.talep.odalar.map((o) => ({ id: yeniOdaKimligi(), yetiskin: o.yetiskin, cocukYaslari: o.cocukYaslari.map(String), ...(o.cocukAylari === undefined ? {} : { cocukAylari: o.cocukAylari.map((a) => (a > 0 ? String(a) : '')) }) }))), [salt, odalar]);
  const gorunenPaketAcik = salt === null ? paketAcik : salt.taslak.paketler;
  const gorunenPaketNotlari = salt === null ? paketNotlari : (salt.taslak.paketNotlari ?? {});
  const gorunenTeklifNotu = salt === null ? teklifNotuMetni : (salt.taslak.teklifNotu ?? '');
  const gorunenKalemler = salt === null ? kalemler : (salt.taslak.serbestKalemler ?? []);
  const gorunenKdv = salt === null ? kdvNotu : (salt.taslak.kdvNotu ?? '');
  const gorunenPlanlama = salt === null ? planlamaMetni : (salt.taslak.planlamaMetni ?? '');
  const gorunenIsleyis = salt === null ? isleyisMetni : (salt.taslak.isleyisMetni ?? '');
  const gorunenYatSaat = salt === null ? yatSaat : String(salt.taslak.yat?.saat ?? YAT_SAAT.varsayilan);
  const gorunenYatBoyut: YatBoyutu = salt === null ? yatBoyut : (salt.taslak.yat?.boyut ?? 'YAT_50FT');
  const coklu = gorunen.length > 1;
  const rs = gorunen.map((b) => raporlar[b.anahtar]);
  const hazirRs = rs.filter((r): r is BolumRaporu => r !== undefined);
  const tarihHatalari = useMemo(() => bolumTarihHatalari(gorunen.map((b) => ({ giris: b.giris, cikis: b.cikis }))), [gorunen]);
  const tarihTamam = tarihHatalari.every((h) => h === null);
  const ilkGiris = gorunen[0]?.giris ?? '';
  const sonCikis = gorunen[gorunen.length - 1]?.cikis ?? '';
  const talepAnahtari = JSON.stringify([gorunen.map((b) => [b.giris, b.cikis]), gorunenOdalar.map((o) => [o.yetiskin, o.cocukYaslari, o.cocukAylari ?? []]), uyruk]);

  // ---- oteller ve destinasyon ----
  const tumOteller = useMemo(() => hazirRs.flatMap((r) => r.oteller), [hazirRs]);
  const destinasyon = destinasyonElle ?? destinasyonOner(tumOteller);
  const baslikOnizleme = musteri !== null ? belgeBasligi(musteri.ad, destinasyon) : null;
  // Paketler otel şehrine bağlı değildir (28.10.2026): hep sunulur, otel eklenip çıkarılınca açık paket kapanmaz.
  const kullanilabilirPaket = paketKullanilabilir();

  // ---- paket fiyatı (tüm grup): ilk girişten son çıkışa ----
  const talepHazir = tarihTamam && yasTamam && uyruk !== '' && musteri !== null;
  const safariAcik = gorunenPaketAcik.includes('SAFARI_TURU');
  const bebekSayisi = odaDuzenleri(gorunenOdalar).flatMap(o => o.cocukYaslari).filter(y => y <= 2).length;
  const safariKoltukSayisi = safariAcik ? Math.min(bebekSayisi, salt === null ? safariKoltuk : (salt.taslak.safariBebekKoltukSayisi ?? 0)) : 0;
  useEffect(() => { if (salt === null) setSafariKoltuk(n => safariAcik ? Math.min(n, bebekSayisi) : 0); }, [salt, safariAcik, bebekSayisi]);
  const yatAcik = gorunenPaketAcik.includes('OZEL_YAT_TURU');
  const yatSaatiDegeri = yatSaatiCoz(gorunenYatSaat);
  const paketImzasi = `${talepAnahtari}|safari:${safariKoltukSayisi}${yatAcik ? `|yat:${gorunenYatSaat}:${gorunenYatBoyut}` : ''}`;
  useEffect(() => {
    if (salt !== null || paketAcik.length === 0 || !talepHazir || (yatAcik && yatSaatiDegeri === null)) return undefined;
    if (paketVeri !== null && paketVeri.imza === paketImzasi && paketAcik.every((k) => paketVeri.fiyatlar.some((f) => f.kod === k))) return undefined;
    const c = new AbortController();
    hizliTeklifApi.paketFiyatlari({ girisTarihi: ilkGiris, cikisTarihi: sonCikis, odalar: odaDuzenleri(odalar), uyruk, kodlar: paketAcik, safariBebekKoltukSayisi: safariKoltukSayisi, ...(yatAcik && yatSaatiDegeri !== null ? { yat: { saat: yatSaatiDegeri, boyut: gorunenYatBoyut } } : {}) }, c.signal)
      .then((v) => { if (!c.signal.aborted) setPaketVeri({ imza: paketImzasi, fiyatlar: v.paketler }); })
      .catch(() => { if (!c.signal.aborted) setPaketVeri({ imza: paketImzasi, fiyatlar: paketAcik.map((kod) => ({ kod, ad: M.paketler[kod], kapsam: M.paketTumGrup, tutarMinor: null, paraBirimi: null })) }); });
    return () => c.abort();
  }, [salt, paketAcik, talepHazir, paketImzasi, ilkGiris, sonCikis, odalar, uyruk, paketVeri, safariKoltukSayisi, yatAcik, yatSaatiDegeri, gorunenYatBoyut]);
  const duzenlemeImzasi = JSON.stringify([musteri?.id, talepAnahtari]);
  const hamKatalogFiyatlari = salt !== null ? (salt.taslak.paketFiyatlari ?? []) : paketVeri !== null && paketVeri.imza === paketImzasi ? paketVeri.fiyatlar : [];
  const tumDuzenlemeler = salt !== null ? (salt.taslak.paketDuzenleme?.degerler ?? {}) : paketDuzenleme?.imza === duzenlemeImzasi ? paketDuzenleme.degerler : {};
  const gorunenDuzenlemeler: PaketDuzenlemeTaslaklari = Object.fromEntries(Object.entries(tumDuzenlemeler).flatMap(([kod, d]) => { const f = hamKatalogFiyatlari.find(f => f.kod === kod && f.paraBirimi === d?.paraBirimi); return d !== undefined && f !== undefined ? [[kod, paketDuzenlemesiniSinirla(d, f)]] : []; }));
  const ilgiliDuzenlemeler = Object.entries(gorunenDuzenlemeler).filter(([kod, d]) => gorunenPaketAcik.includes(kod as PaketKodu) && hamKatalogFiyatlari.some(f => f.kod === kod && f.paraBirimi === d?.paraBirimi));
  const cozulenDuzenlemeler = ilgiliDuzenlemeler.map(([kod, d]) => paketDuzenlemesiCoz(kod as PaketKodu, d as PaketDuzenlemeTaslagi));
  const paketDuzenlemeGecerli = cozulenDuzenlemeler.every(d => d !== null);
  const paketFiyatlariHam = hamKatalogFiyatlari.map(f => paketFiyatiniDuzenle(f, gorunenDuzenlemeler[f.kod], odaToplamlari(gorunenOdalar).yetiskin, odaDuzenleri(gorunenOdalar).flatMap(o => o.cocukYaslari), safariKoltukSayisi));
  const paketDuzenle = (kod: PaketKodu, d: PaketDuzenlemeTaslagi | undefined) => setPaketDuzenleme({ imza: duzenlemeImzasi, degerler: { ...tumDuzenlemeler, [kod]: d } });
  // Araç tipi (4 kişilik, 6 kişilik, karışık): açık paketlerin seçenekleri toplanır, varsayılan en ucuzdur.
  const acikSecenekliler = paketFiyatlariHam.filter((f) => gorunenPaketAcik.includes(f.kod) && f.secenekler !== undefined);
  const aracAnahtarlari: readonly AracSecimi[] = acikSecenekliler[0]?.secenekler?.map((s) => s.anahtar) ?? [];
  const aracToplam = (a: AracSecimi): number => acikSecenekliler.reduce((t, f) => t + (f.secenekler?.find((s) => s.anahtar === a)?.tutarMinor ?? 0), 0);
  const aracOnerisi: AracSecimi | null = [...aracAnahtarlari].sort((a, b) => aracToplam(a) - aracToplam(b))[0] ?? null;
  const aracEfektif: AracSecimi | null = aracAnahtarlari.length === 0 ? null : (aracSecimi !== null && aracAnahtarlari.includes(aracSecimi) ? aracSecimi : aracOnerisi);
  const aracGorunumleri: AracGorunumu[] = aracAnahtarlari.map((a) => ({
    anahtar: a,
    etiket: acikSecenekliler[0]?.secenekler?.find((s) => s.anahtar === a)?.etiket ?? a,
    tutar: paraYaz(aracToplam(a), acikSecenekliler[0]?.paraBirimi ?? 'USD'),
    oneri: a === aracOnerisi,
  }));
  const paketFiyatlari: readonly PaketFiyati[] = paketFiyatlariHam.map((f) => {
    const s = aracEfektif === null ? undefined : f.secenekler?.find((x) => x.anahtar === aracEfektif);
    return s === undefined ? f : { ...f, tutarMinor: s.tutarMinor, kapsam: s.aciklama };
  });

  const paketler: PaketGorunumu[] = PAKET_KODLARI.filter((k) => kullanilabilirPaket.includes(k)).map((kod) => {
    const acik = gorunenPaketAcik.includes(kod);
    const f = paketFiyatlari.find((x) => x.kod === kod);
    const bekleniyor = acik && salt === null && paketVeri?.imza !== paketImzasi;
    const fiyatYok = acik && !bekleniyor && (f === undefined || f.tutarMinor === null || f.paraBirimi === null);
    return { kod, acik, fiyatYok, cocukFiyatiYok: f?.cocukFiyatiYok === true, fiyat: bekleniyor ? M.paketFiyatYukleniyor : fiyatYok ? M.paketFiyatYok : f !== undefined && f.tutarMinor !== null && f.paraBirimi !== null ? paraYaz(f.tutarMinor, f.paraBirimi) : '' };
  });

  // ---- işaretli satırlar, kapı ----
  const isaretliHepsi = hazirRs.flatMap((r) => r.isaretli);
  const manuelHepsi = hazirRs.flatMap((r) => r.manuelIsaretli);
  const bolumSecimleri = rs.map((r) => (r?.isaretli.length ?? 0) + (r?.manuelIsaretli.length ?? 0));
  const surer = hazirRs.some((r) => r.calisiyor);
  const eskiTalep = hazirRs.some((r) => r.eskiTalep);
  const sonucVar = hazirRs.some((r) => r.sonucVar);
  const paralar = new Set([...isaretliHepsi.map((s) => s.durum.hesap?.paraBirimi ?? null), ...manuelHepsi.map((x) => x.manuel.paraBirimi)]);
  const paraTamam = !coklu || (paralar.size <= 1 && !paralar.has(null));
  const ilkPara = isaretliHepsi[0]?.durum.hesap?.paraBirimi ?? manuelHepsi[0]?.manuel.paraBirimi ?? null;
  // Tek bölümde paket her seçeneğin içinde (satırın para birimiyle); çoklu bölümde planın tamamı için bir kez.
  const satirPaketleri = isaretliHepsi.map((s) => ({ s, p: paketDurumu(gorunenPaketAcik, paketFiyatlari, s.durum.hesap?.paraBirimi ?? null) }));
  const manuelPaketleri = manuelHepsi.map((x) => ({ x, p: paketDurumu(gorunenPaketAcik, paketFiyatlari, x.manuel.paraBirimi) }));
  const planPaket = paketDurumu(gorunenPaketAcik, paketFiyatlari, ilkPara);
  const paketVerisiHazir = gorunenPaketAcik.length === 0 || (salt !== null && hamKatalogFiyatlari.length > 0) || (paketVeri !== null && paketVeri.imza === paketImzasi);
  const paketTamam = (coklu ? planPaket.tamam : (satirPaketleri.every((x) => x.p.tamam) && manuelPaketleri.every((x) => x.p.tamam))) && paketVerisiHazir && paketDuzenlemeGecerli;

  // ---- serbest kalemler (28.10.2026): kapsamı teklifte işaretli otel seçeneklerinden biri ya da tümü ----
  const secenekListesi: KalemSecenegi[] = [
    ...isaretliHepsi.map((s) => ({ anahtar: s.sonuc.slug ?? s.otel.slug, ad: s.sonuc.otelAdi ?? s.otel.ad })),
    ...manuelHepsi.map((x) => ({ anahtar: manuelAnahtar(x.manuel.id), ad: x.manuel.ad.trim() })),
  ];
  // Hiçbir seçenek işaretli değilken kapsam hatası gösterilmez (çıktı zaten kapalıdır): kalemin kendi seçeneği geçerli sayılır.
  const kalemAnahtarlari = secenekListesi.length === 0 ? gorunenKalemler.flatMap((k) => (k.secenek === null ? [] : [k.secenek])) : secenekListesi.map((a) => a.anahtar);
  const kalemDegerleri = Object.fromEntries(gorunenKalemler.map((k) => [k.id, kalemDegerlendir(k, ilkPara, kalemAnahtarlari)]));
  const kalemT = kalemToplamlari(gorunenKalemler, ilkPara, kalemAnahtarlari);
  const kalemGecersiz = gorunenKalemler.length > 0 && kalemT.hatali > 0;
  const yatGecersiz = yatAcik && yatSaatiDegeri === null;
  const yatGorunumu: YatGorunumu = (() => {
    const f = paketFiyatlariHam.find((x) => x.kod === 'OZEL_YAT_TURU');
    return {
      saat: gorunenYatSaat,
      saatGecerli: yatSaatiDegeri !== null,
      boyut: gorunenYatBoyut,
      secenekler: YAT_BOYUTLARI.map((b) => {
        const x = f?.yat?.secenekler.find((o) => o.anahtar === b);
        return { anahtar: b, ad: M.yat.boyutlar[b], saatBasi: x === undefined || f?.paraBirimi === undefined || f.paraBirimi === null ? '' : paraYaz(x.saatBasiMinor, f.paraBirimi) };
      }),
      toplam: f !== undefined && f.tutarMinor !== null && f.paraBirimi !== null && yatSaatiDegeri !== null ? M.yat.toplam(yatSaatiDegeri, paraYaz(f.tutarMinor, f.paraBirimi)) : '',
      saatDegistir: setYatSaat,
      boyutDegistir: setYatBoyut,
    };
  })();
  const konaklamaTutarlar = [...isaretliHepsi.map(s => s.durum.hesap?.toplamMinor ?? 0), ...manuelHepsi.map(x => x.toplamMinor ?? 0)];
  const enYuksekKonaklama = coklu ? konaklamaTutarlar.reduce((t, n) => t + n, 0) : Math.max(0, ...konaklamaTutarlar);
  const enYuksekEk = kalemT.tumMinor + (coklu ? [...kalemT.secenekMinor.values()].reduce((t, n) => t + n, 0) : Math.max(0, ...kalemT.secenekMinor.values()));
  const paketToplam = paketFiyatlari.filter(f => gorunenPaketAcik.includes(f.kod)).reduce((t, f) => t + (f.tutarMinor ?? 0), 0);
  const toplamSiniriAsildi = !Number.isSafeInteger(enYuksekKonaklama + enYuksekEk + paketToplam) || enYuksekKonaklama + enYuksekEk + paketToplam > 2_000_000_000;
  const kapi = ciktiKapisi({
    sonucVar,
    cekimBitti: !surer,
    yasTamam,
    talepGuncel: !eskiTalep,
    salt: saltOkunur,
    paketTamam: paketTamam && !toplamSiniriAsildi,
    tarihTamam,
    paraTamam,
    bolumSecimleri,
    tekSecim: coklu,
    isaretli: isaretliHepsi.map((s) => s.durum),
    manuelSecim: manuelHepsi.length,
    manuelGecersiz: manuelHepsi.some((x) => !x.gecerli),
    kalemGecersiz,
    yatGecersiz,
  });
  const nedenMetni = kapi.neden === null ? null : M.neden[kapi.neden];

  // ---- özet ----
  const ekOlcusu = (anahtar: string): number => kalemT.tumMinor + (kalemT.secenekMinor.get(anahtar) ?? 0);
  const katalogSatirlari = satirPaketleri.filter(({ s }) => s.durum.hesap?.toplamMinor !== undefined && s.durum.hesap.toplamMinor !== null).map(({ s, p }) => {
    const top = s.durum.hesap === null ? null : secenekToplami(s.durum.hesap, p);
    const ek = ekOlcusu(s.sonuc.slug ?? s.otel.slug);
    return {
      girdi: s.otel.girdi,
      ad: s.sonuc.otelAdi ?? s.otel.ad,
      kombinasyon: (s.durum.hesap === null ? '' : kombinasyonAdi(s.durum.hesap.secilenler)) + (s.durum.kod === 'sure' ? `. ${M.guncelDegil}` : ''),
      konaklamaMinor: top?.konaklamaMinor ?? 0,
      paketMinor: top?.paketMinor ?? 0,
      ekMinor: ek,
      toplamMinor: top === null || top.toplamMinor === null ? null : top.toplamMinor + ek,
      para: top?.para ?? 'USD',
    };
  });
  const manuelSatirlari = manuelPaketleri.filter(({ x }) => x.toplamMinor !== null).map(({ x, p }) => {
    const top = secenekToplami({ secilenler: [], uymayanOdalar: [], toplamMinor: x.toplamMinor, paraBirimi: x.manuel.paraBirimi, paraKarisik: false }, p);
    const ek = ekOlcusu(manuelAnahtar(x.manuel.id));
    return {
      girdi: manuelAnahtar(x.manuel.id),
      ad: x.manuel.ad.trim(),
      kombinasyon: x.manuel.odaAdi.trim(),
      konaklamaMinor: x.toplamMinor ?? 0,
      paketMinor: top?.paketMinor ?? 0,
      ekMinor: ek,
      toplamMinor: top === null || top.toplamMinor === null ? null : top.toplamMinor + ek,
      para: x.manuel.paraBirimi,
    };
  });
  const tekSatirlari = [...katalogSatirlari, ...manuelSatirlari];
  const aralik = ozetAraligi(tekSatirlari.flatMap((o) => (o.toplamMinor === null ? [] : [o.toplamMinor])));
  const ozetPara = tekSatirlari[0]?.para ?? null;

  const plan: PlanGorunumu | null = coklu ? (() => {
    const satir = gorunen.map((b, i) => {
      const s = rs[i]?.isaretli[0];
      const m = rs[i]?.manuelIsaretli[0];
      const hesap = s?.durum.hesap ?? null;
      return { anahtar: b.anahtar, no: i + 1, ad: s !== undefined ? (s.sonuc.otelAdi ?? s.otel.ad) : (m === undefined ? null : m.manuel.ad.trim()), gece: gunSayisi(b.giris, b.cikis), tarih: b.giris !== '' && b.cikis !== '' ? tarihAraligiKisa(b.giris, b.cikis) : M.bolum.tarihYok, toplamMinor: hesap?.toplamMinor ?? m?.toplamMinor ?? null, para: hesap?.paraBirimi ?? m?.manuel.paraBirimi ?? 'USD', anahtarSecenek: s !== undefined ? (s.sonuc.slug ?? s.otel.slug) : (m === undefined ? null : manuelAnahtar(m.manuel.id)) };
    });
    const tamam = satir.every((x) => x.toplamMinor !== null) && paraTamam && planPaket.tamam && paketVerisiHazir && paketDuzenlemeGecerli;
    const planEk = kalemT.tumMinor + satir.reduce((t, x) => t + (x.anahtarSecenek === null ? 0 : (kalemT.secenekMinor.get(x.anahtarSecenek) ?? 0)), 0);
    const genel = tamam ? satir.reduce((t, x) => t + (x.toplamMinor ?? 0), 0) + planPaket.acik.reduce((t, p) => t + (p.tutarMinor ?? 0), 0) + planEk : null;
    return {
      satirlar: satir.map((x) => ({ anahtar: x.anahtar, no: x.no, ad: x.ad, gece: x.gece, tarih: x.tarih, toplamMinor: x.toplamMinor, para: x.para })),
      paketler: [...planPaket.acik.map((p) => ({ ad: p.ad, tutarMinor: p.tutarMinor })), ...planPaket.fiyatsiz.map((p) => ({ ad: p.ad, tutarMinor: null }))],
      ekMinor: planEk,
      genelToplamMinor: genel,
      para: ilkPara,
      paraKarisik: !paraTamam,
    };
  })() : null;

  const ozetUyarilari: OzetUyari[] = [];
  for (const s of isaretliHepsi) {
    const ad = s.sonuc.otelAdi ?? s.otel.ad;
    if (s.durum.sure === 'kritik' && s.durum.kalan !== null) ozetUyarilari.push({ ton: 'tehlike', metin: M.ozetSureKritik(ad, kalanMetni(s.durum.kalan)) });
    else if (s.durum.sure === 'bilgi' && s.durum.kalan !== null) ozetUyarilari.push({ ton: 'uyari', metin: M.ozetSureUyari(ad, kalanMetni(s.durum.kalan)) });
    else if (s.durum.kod === 'sure') ozetUyarilari.push({ ton: 'tehlike', metin: M.ozetSureDoldu(ad) });
  }
  const fiyatsizKodlar = new Set(coklu ? planPaket.fiyatsiz.map((f) => f.kod) : satirPaketleri.flatMap((x) => x.p.fiyatsiz.map((f) => f.kod)));
  if (isaretliHepsi.length > 0 && paketVerisiHazir) for (const kod of fiyatsizKodlar) ozetUyarilari.push({ ton: 'uyari', metin: M.ozetPaketFiyatsiz(M.paketler[kod]) });
  tarihHatalari.forEach((h, i) => { if (h === 'ortusme') ozetUyarilari.push({ ton: 'tehlike', metin: M.bolum.ortusme(i + 1) }); });

  // ---- çıktı gövdesi (yalnız kapı açıkken kurulur) ----
  const secimGovdesi = kapi.acik && musteri !== null
    ? teklifSecimiKur({
      musteriId: musteri.id,
      musteriAd: musteri.ad,
      destinasyon,
      odalar: odaDuzenleri(gorunenOdalar),
      uyruk,
      planPaket,
      safariBebekKoltukSayisi: safariKoltukSayisi,
      paketDuzenlemeleri: cozulenDuzenlemeler.filter((d): d is NonNullable<typeof d> => d !== null),
      teklifNotu: gorunenTeklifNotu,
      paketNotlari: gorunenPaketNotlari,
      serbestKalemler: gorunenKalemler,
      kdvNotu: gorunenKdv,
      planlamaMetni: gorunenPlanlama,
      isleyisMetni: gorunenIsleyis,
      ...(yatAcik && yatSaatiDegeri !== null ? { yat: { saat: yatSaatiDegeri, boyut: gorunenYatBoyut } } : {}),
      ...(aracEfektif === null ? {} : { aracSecimi: aracEfektif }),
      bolumler: gorunen.map((b, i) => ({
        fiyatIsiId: rs[i]?.isId ?? null,
        anahtar: b.anahtar.replace(/^surum-/, ''),
        etiket: M.bolum.etiket(i + 1),
        giris: b.giris,
        cikis: b.cikis,
        satirlar: (rs[i]?.isaretli ?? []).map((s) => ({ ...(s.ekNot === undefined ? {} : { ekNot: s.ekNot }), ...(s.satisToplamMinor === undefined ? {} : { satisToplamMinor: s.satisToplamMinor }), isId: s.isId, otel: s.otel, sonuc: s.sonuc, durum: s.durum, paket: paketDurumu(gorunenPaketAcik, paketFiyatlari, s.durum.hesap?.paraBirimi ?? null) })),
        manuelSatirlar: (rs[i]?.manuelIsaretli ?? []).map((x) => ({ manuel: x.manuel, toplamMinor: x.toplamMinor ?? 0, paket: paketDurumu(gorunenPaketAcik, paketFiyatlari, x.manuel.paraBirimi) })),
      })),
    })
    : null;
  // Araç tipi mesajdaki gezi toplamını değiştirir: seçim değişince mesaj eskimiş sayılır.
  const secimImza = secimGovdesi === null ? '' : `${secimImzasi(secimGovdesi)}|${pdfDili}`;
  const mesajEskimis = mesajMetni !== '' && secimImza !== '' && mesajKaynagi !== secimImza;
  const mesajGuncelDegil = mesajMetni !== '' && (kapi.neden === 'sureDoldu' || kapi.neden === 'eski');

  // ---- bölüm işlemleri ----
  const rapor = useCallback((r: BolumRaporu) => setRaporlar((m) => ({ ...m, [r.anahtar]: r })), []);
  const bolumBitti = useCallback(() => undefined, []);
  const aralikDegistir = useCallback((anahtar: string, giris: string, cikis: string) => setBolumler((l) => l.map((b) => (b.anahtar === anahtar ? { ...b, giris, cikis } : b))), []);

  const bolumEkle = useCallback(() => {
    if (bolumler.length >= AZAMI_BOLUM) { duyur(M.bolum.limit); return; }
    bolumSayaci.current += 1;
    const anahtar = `b${bolumSayaci.current}`;
    const onceki = bolumler[bolumler.length - 1];
    setBolumler((l) => [...l, { anahtar, giris: onceki?.cikis ?? '', cikis: '' }]);
    setAktifAnahtar(anahtar);
    duyur(M.bolum.eklendi(bolumler.length + 1));
  }, [bolumler, duyur]);

  const bolumSil = useCallback((anahtar: string) => {
    const i = bolumler.findIndex((b) => b.anahtar === anahtar);
    if (i < 0 || bolumler.length < 2) return;
    const kalan = bolumler.filter((b) => b.anahtar !== anahtar);
    setBolumler(kalan);
    setRaporlar((m) => { const n = { ...m }; delete n[anahtar]; return n; });
    setAktifAnahtar(kalan[Math.min(i, kalan.length - 1)]?.anahtar ?? 'b1');
    setMesajMetni(''); setMesajKaynagi('');
    duyur(M.bolum.silindi(i + 1));
  }, [bolumler, duyur]);

  // Ekran durumundan taslak: sürüm kaydı ve tarayıcı taslağı aynı şekli kullanır.
  const taslakOlustur = useCallback((): TeklifTaslagi => ({ safariBebekKoltukSayisi: safariKoltukSayisi, paketFiyatlari: hamKatalogFiyatlari, paketDuzenleme: { imza: duzenlemeImzasi, degerler: gorunenDuzenlemeler }, ...(aracSecimi === null ? {} : { aracSecimi }), ...(kalemler.length === 0 ? {} : { serbestKalemler: kalemler }), ...(kdvNotu.trim() === '' ? {} : { kdvNotu }), ...(planlamaMetni.trim() === '' ? {} : { planlamaMetni }), ...(isleyisMetni.trim() === '' ? {} : { isleyisMetni }), ...(paketAcik.includes('OZEL_YAT_TURU') ? { yat: { saat: yatSaatiCoz(yatSaat) ?? YAT_SAAT.varsayilan, boyut: yatBoyut } } : {}), ...(teklifNotuMetni.trim() === '' ? {} : { teklifNotu: teklifNotuMetni }), ...(Object.values(paketNotlari).some((v) => v !== undefined && v.trim() !== '') ? { paketNotlari } : {}), talep: { odalar: odaDuzenleri(odalar), uyruk }, paketler: paketAcik, bolumler: bolumler.flatMap((b) => { const r = raporlar[b.anahtar]; return r === undefined ? [] : [r.taslak]; }) }), [odalar, uyruk, paketAcik, bolumler, raporlar, teklifNotuMetni, paketNotlari, aracSecimi, kalemler, kdvNotu, planlamaMetni, isleyisMetni, yatSaat, yatBoyut, safariKoltukSayisi, hamKatalogFiyatlari, duzenlemeImzasi, gorunenDuzenlemeler]);

  // Tarayıcı taslağı (03.11.2026): kayıt ucu açık talep ister; talep yoksa sunucu kaydı 409 verir ve ekran sayfa yenilenince
  // boşalıyordu. Taslak müşteri başına 14 gün localStorage'da durur ve "Kayıtlı taslağı geri yükle" ile açılır.
  const [kayitliTaslak, setKayitliTaslak] = useState<SaklananTaslak | null>(null);
  useEffect(() => {
    if (musteriId === null) { setKayitliTaslak(null); return; }
    try { setKayitliTaslak(taslakOku(window.localStorage, musteriId, Date.now())); } catch { setKayitliTaslak(null); }
  }, [musteriId]);
  useEffect(() => {
    if (musteri === null || salt !== null || bolumler.every((b) => raporlar[b.anahtar] === undefined)) return undefined;
    const t = window.setTimeout(() => {
      try { taslakYaz(window.localStorage, musteri.id, taslakOlustur(), Date.now(), { destinasyon: destinasyonElle, pdfDili, ton }); } catch { /* depo kapalı: sessiz geç */ }
    }, 1500);
    return () => window.clearTimeout(t);
  }, [musteri, salt, bolumler, raporlar, taslakOlustur, destinasyonElle, pdfDili, ton]);
  // Taslağı düzenlenebilir ekran durumu olarak geri kurar (salt okunur sürüm görüntülemeden ayrı).
  const [geriKurulan, setGeriKurulan] = useState<Readonly<Record<string, TaslakBolum>> | null>(null);
  const taslakGeriYukle = useCallback(() => {
    if (kayitliTaslak === null) return;
    const d = taslagiGeriKur(kayitliTaslak.taslak, musteriId ?? '', kayitliTaslak.ekran);
    setSalt(null);
    setOdalar(d.odalar); setHizli(kanonikHizli(d.odalar));
    bolumSayaci.current = d.bolumSayaci;
    setBolumler(d.bolumler); setAktifAnahtar(d.aktifAnahtar); setRaporlar({});
    setUyrukSecimi(d.uyruk); setPaketAcik(d.paketAcik); setPaketNotlari(d.paketNotlari); setPaketVeri(null);
    setPaketDuzenleme(d.paketDuzenleme); setTeklifNotuMetni(d.teklifNotu); setAracSecimi(d.aracSecimi);
    setKalemler(d.kalemler); setKdvNotu(d.kdvNotu); setPlanlamaMetni(d.planlamaMetni); setIsleyisMetni(d.isleyisMetni);
    setSafariKoltuk(d.safariKoltuk); setYatSaat(d.yatSaat); setYatBoyut(d.yatBoyut);
    setDestinasyonElle(d.destinasyon); setPdfDili(d.pdfDili); setTon(d.ton);
    setMesajMetni(''); setMesajKaynagi(''); setCikti(null);
    setGeriKurulan(d.bolumVeri); setKayitliTaslak(null);
    duyur(M.taslakGeriYuklendi);
  }, [kayitliTaslak, musteriId, duyur]);

  // Sürüm kaydı: PDF ya da mesaj oluşturulduğunda (seçim, fiyatlar ve metin birlikte donar). Kayıt başarısız olursa çıktı
  // yine verilir; ekran bunu açıkça söyler. Sunucu aynı içeriği ikinci kez sürüm yapmaz.
  const surumKaydet = useCallback(async (secim: TeklifSecimi, mesaj: string, tonu: MesajTonu) => {
    if (salt !== null) return;
    const taslak = taslakOlustur();
    try {
      const s = await hizliTeklifApi.surumKaydet({ secim, taslak, mesaj: mesaj.trim() === '' ? null : { metin: mesaj, ton: tonu } });
      setSurumler((l) => [...l.filter((x) => x.id !== s.id), s]); setSurumHata(null);
    } catch (e: unknown) {
      const m = ucYok(e) ? M.uclarYok : (kayitHataCumlesi(e) ?? M.surumKaydedilemedi);
      setSurumHata(m); setCikti(M.surumKaydedilemediCikti(m)); duyur(M.surumKaydedilemediCikti(m));
    }
  }, [salt, odalar, uyruk, paketAcik, bolumler, raporlar, teklifNotuMetni, paketNotlari, aracSecimi, kalemler, kdvNotu, planlamaMetni, isleyisMetni, yatSaat, yatBoyut, safariKoltukSayisi, hamKatalogFiyatlari, duzenlemeImzasi, gorunenDuzenlemeler]);

  useEffect(() => {
    if (musteri === null) { setSurumler([]); return undefined; }
    const c = new AbortController();
    hizliTeklifApi.surumler(musteri.id, c.signal).then((v) => { if (!c.signal.aborted) { setSurumler(v.surumler); setSurumHata(null); } }).catch((e: unknown) => { if (!c.signal.aborted) { setSurumler([]); setSurumHata(ucYok(e) ? M.uclarYok : M.surumYuklenemedi); } });
    return () => c.abort();
  }, [musteri]);

  const surumAc = useCallback((s: SurumOzeti) => {
    if (musteri === null) return;
    hizliTeklifApi.surum(s.teklifId, s.surumNo).then((k) => { setSalt({ no: k.surumNo, taslak: k.taslak }); setCikti(null); duyur(M.surumAciklama(k.surumNo)); }).catch(() => setSurumHata(M.surumYuklenemedi));
  }, [musteri, duyur]);

  // ---- talep etkileşimleri ----
  const odaDegistir = useCallback((odaId: string, yama: { yetiskin?: string; cocukYaslari?: string[]; cocukAylari?: string[] }) => {
    setOdalar((v) => v.map((o) => (o.id === odaId ? { ...o, ...(yama.yetiskin !== undefined ? { yetiskin: Number(yama.yetiskin) } : {}), ...(yama.cocukYaslari !== undefined ? { cocukYaslari: yama.cocukYaslari } : {}), ...(yama.cocukAylari !== undefined ? { cocukAylari: yama.cocukAylari } : {}) } : o)));
    setHizliHata(null);
  }, []);

  const hizliUygula = useCallback(() => {
    if (hizli.trim() === '') return true;
    const y = hizliYazimiCoz(hizli);
    if (!y.tamam) { setHizliHata(M.hizliHata); return false; }
    const yeni = hizliYazimiUygula(odalar, y);
    setOdalar(yeni); setHizliHata(null); setHizli(kanonikHizli(yeni));
    return true;
  }, [hizli, odalar]);

  const odalariUygula = useCallback((yeni: OdaTaslagi[]) => { setOdalar(yeni); setHizli(kanonikHizli(yeni)); }, []);
  const paketDegistir = useCallback((kod: PaketKodu, acik: boolean) => setPaketAcik((l) => (acik ? [...l.filter((k) => k !== kod), kod] : l.filter((k) => k !== kod))), []);

  const kisayol = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (hizliUygula()) for (const r of hazirRs) r.cek();
    }
  }, [hizliUygula, hazirRs]);

  // ---- çıktı eylemleri ----
  const pdfYap = useCallback(async () => {
    if (!kapi.acik || secimGovdesi === null) return;
    setMesgul('pdf'); setCikti(null);
    try {
      const { blob, dosyaAdi } = await hizliTeklifApi.pdf(secimGovdesi, ton, undefined, pdfDili);
      const adres = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = adres; a.download = dosyaAdi; document.body.append(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(adres), 10_000);
      setCikti(M.pdfHazir); duyur(M.pdfHazir);
      await surumKaydet(secimGovdesi, mesajEskimis ? '' : mesajMetni, ton);
    } catch (e) { const m = ucYok(e) ? M.uclarYok : (sunucuCumlesi(e) ?? M.pdfHata); setCikti(m); duyur(m); } finally { setMesgul(null); }
  }, [kapi.acik, secimGovdesi, duyur, ton, mesajMetni, surumKaydet, pdfDili, mesajEskimis]);

  const mesajYap = useCallback(async (tonu: MesajTonu = ton) => {
    if (!kapi.acik || secimGovdesi === null) return;
    setMesgul('mesaj'); setCikti(null);
    try {
      const v = await hizliTeklifApi.mesaj(secimGovdesi, tonu, undefined, pdfDili);
      setMesajMetni(v.metin); setMesajKaynagi(`${secimImzasi(secimGovdesi)}|${pdfDili}`); duyur(M.mesajHazir);
      await surumKaydet(secimGovdesi, v.metin, tonu);
    } catch (e) { const m = ucYok(e) ? M.uclarYok : (sunucuCumlesi(e) ?? M.mesajHata); setCikti(m); duyur(m); } finally { setMesgul(null); }
  }, [kapi.acik, secimGovdesi, ton, duyur, surumKaydet, pdfDili]);

  // Kritik eşik (2 dk): çıktı oluştururken onay istenir.
  const istek = useCallback((tur: 'pdf' | 'mesaj') => {
    if (!kapi.acik) return;
    if (kritikVar(isaretliHepsi.map((s) => s.durum))) { setOnayBekliyor(tur); return; }
    if (tur === 'pdf') void pdfYap(); else void mesajYap();
  }, [kapi.acik, isaretliHepsi, pdfYap, mesajYap]);

  const kopyala = useCallback(async () => {
    if (!kapi.acik || mesajMetni === '' || mesajEskimis) return;
    try {
      await navigator.clipboard.writeText(mesajMetni);
      setCikti(M.mesajKopyalandi); duyur(M.mesajKopyalandi);
    } catch { setCikti(M.mesajKopyalanamadi); duyur(M.mesajKopyalanamadi); }
  }, [kapi.acik, mesajMetni, mesajEskimis, duyur]);

  // ---- çizim ----
  const hataliYaslar = useMemo(() => new Set(eksikler.map((e) => `${e.oda}:${e.cocuk}`)), [eksikler]);
  const yasHataMetni = eksikler.length === 0 ? '' : `Çocuk yaşı eksik: ${eksikler.map((e) => `Oda ${e.oda + 1}, ${e.cocuk + 1}. çocuk`).join('; ')}.`;
  const taslakMeta = surumler.length > 0 ? (() => { const son = [...surumler].sort((a, b) => b.surumNo - a.surumNo)[0]; return son === undefined ? null : M.taslakMeta(son.surumNo, saatYaz(son.kayitZamani)); })() : null;
  const odaMetniOzet = M.odaOzeti(toplamlar.oda, toplamlar.yetiskin, toplamlar.cocuk);
  const tekGece = !coklu ? gunSayisi(ilkGiris, sonCikis) : null;
  const geceMetni = tekGece !== null ? `, ${M.odaOzetiGece(tekGece)}` : '';
  const aktifSecili = gorunen.some((b) => b.anahtar === aktifAnahtar) ? aktifAnahtar : (gorunen[0]?.anahtar ?? 'b1');
  const paketAlani = <PaketAlani safariKoltuk={{ bebekSayisi, adet: safariKoltukSayisi, degistir: setSafariKoltuk }} toplamSiniriAsildi={toplamSiniriAsildi} fiyatlar={paketFiyatlariHam} duzenlemeler={gorunenDuzenlemeler} duzenle={paketDuzenle} paketler={paketler} degistir={paketDegistir} yokBilgisi={false} devreDisi={saltOkunur} notlar={gorunenPaketNotlari} notDegistir={(kod, not) => setPaketNotlari((m) => ({ ...m, [kod]: not }))} araclar={aracGorunumleri} aracSecili={aracEfektif} aracDegistir={setAracSecimi} yat={yatGorunumu} />;

  return (
    <div className="ht-ekran" data-testid="hizli-teklif">
      <a className="ht-atla" href="#ht-sonuc" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLElement>('[data-testid="ht-sonuclar"] h2')?.focus(); }}>{M.atla}</a>
      <div className="ht-sr" role="status" aria-live="polite" aria-atomic="true" data-testid="ht-canli">{canli}</div>
      <div className="ht-ust">
        {taslakMeta !== null ? <span className="ht-meta"><Ikon ad="saat" kucuk />{taslakMeta}</span> : null}
        {salt !== null ? <span className="ht-meta ht-meta-uyari" data-testid="ht-salt-bant">{M.surumAciklama(salt.no)}</span> : null}
        {cikti !== null ? <span className="ht-meta" data-testid="ht-cikti-bilgi">{cikti}</span> : null}
      </div>
      <div className="ht-izgara">
        <div className="ht-sol" id="ht-sonuc">
          <section className="ht-kart" aria-labelledby={`${kimlik}-talep`} onKeyDown={kisayol} data-testid="ht-talep">
            <div className="ht-kart-ust"><h2 id={`${kimlik}-talep`}>{M.talepBaslik}</h2><span className="ht-ipucu">{M.talepIpucu}</span></div>
            <div className="ht-talep-satir">
              <MusteriAlani ekleyebilir={musteriEkleyebilir} musteri={musteri === null ? null : { ...musteri, uyruk: uyruk === '' ? null : uyruk }}yukleniyor={musteriYuk} hata={musteriHata} sec={(id) => musteriSec(id)} degistir={() => musteriSec(null)} devreDisi={saltOkunur} />
              <div className="ht-alan">
                {coklu ? (
                  <><span className="ht-etiket">{M.tarihler}</span><span className="ht-ipucu" data-testid="ht-tarihler-bolumde">{M.bolum.tarihlerBolumde}</span></>
                ) : (
                  <>
                    <label htmlFor={`${kimlik}-tarih`}>{M.tarihler}</label>
                    <TarihAraligiSecici id={`${kimlik}-tarih`} testId="ht-tarih" giris={ilkGiris} cikis={sonCikis} setAralik={(g, c) => { if (!saltOkunur) aralikDegistir(bolumler[0]?.anahtar ?? 'b1', g, c); }} />
                  </>
                )}
              </div>
              <div className="ht-alan">
                <label htmlFor={`${kimlik}-hizli`}>{M.hizliYaz}</label>
                <div className="ht-girdi">
                  <Ikon ad="yatak" />
                  <input
                    id={`${kimlik}-hizli`}
                    value={hizli}
                    placeholder={M.hizliOrnek}
                    disabled={saltOkunur}
                    autoComplete="off"
                    aria-invalid={hizliHata !== null ? 'true' : undefined}
                    aria-describedby={hizliHata !== null ? `${kimlik}-hizli-hata` : undefined}
                    data-testid="ht-hizli"
                    onChange={(e) => { setHizli(e.target.value); setHizliHata(null); }}
                    onBlur={() => { if (hizli.trim() !== '') hizliUygula(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); hizliUygula(); } }}
                  />
                </div>
                {hizliHata !== null ? <span className="ht-hata-yazi" id={`${kimlik}-hizli-hata`}>{hizliHata}</span> : null}
              </div>
            </div>
            <OdaDagilimi
              odalar={gorunenOdalar}
              onek="ht"
              devreDisi={saltOkunur}
              metin={{ oda: (n) => `Oda ${n}`, yetiskin: 'Yetişkin', cocuk: 'Çocuk', cocukYasi: (n) => `${n}. çocuk yaşı`, yasSec: 'Yaş seçin', odaKaldir: (n) => `Oda ${n} sil` }}
              siniflar={{ kap: 'ht-odalar', oda: 'ht-oda', alanlar: 'ht-oda-alanlar', yaslar: 'ht-oda-yaslar', kaldir: 'ht-ikon-dugme' }}
              hataliYaslar={hataliYaslar}
              hataMetniId={`${kimlik}-yas-hata`}
              degistir={odaDegistir}
              ayMetni={{ etiket: M.ayEtiket, sec: M.aySec }}
              kaldir={(odaId) => setOdalar((v) => v.filter((o) => o.id !== odaId))}
            />
            {yasHataMetni !== '' ? <p className="ht-hata-yazi" id={`${kimlik}-yas-hata`} data-testid="ht-yas-hata">{yasHataMetni}</p> : null}
            <div className="ht-oda-eylem">
              <button type="button" className="ht-btn" disabled={saltOkunur || odalar.length >= POLITIKA.azamiOda} onClick={() => setOdalar((v) => [...v, yeniOda(2)])} data-testid="ht-oda-ekle"><Ikon ad="arti" kucuk />{M.odaEkle}</button>
              <button type="button" className="ht-btn" disabled={saltOkunur} aria-disabled={bolumler.length >= AZAMI_BOLUM} onClick={bolumEkle} data-testid="ht-bolum-ekle"><Ikon ad="arti" kucuk />{M.bolum.ekle.replace(/^\+ /, '')}</button>
              <span className="ht-ipucu">{odaMetniOzet}{geceMetni}. {M.hizliYazimi}: <kbd>{kanonikHizli(odalar)}</kbd></span>
            </div>
            <div className="ht-alan ht-uyruk-alani">
              <label htmlFor={`${kimlik}-uyruk`}>{M.uyrukEtiket}</label>
              <select id={`${kimlik}-uyruk`} value={uyruk} disabled={saltOkunur} data-testid="ht-uyruk-sec" onChange={(e) => setUyrukSecimi(e.target.value)}>
                <option value="">{M.uyrukSec}</option>
                {UYRUK_KODLARI.map((k) => <option key={k} value={k}>{`${k} · ${bolgeAdi(k)}`}</option>)}
                {uyruk !== '' && !UYRUK_KODLARI.includes(uyruk) ? <option value={uyruk}>{`${uyruk} · ${bolgeAdi(uyruk)}`}</option> : null}
              </select>
            </div>
            <div className="ht-alan ht-destinasyon">
              <label htmlFor={`${kimlik}-dest`}>{M.destinasyon}</label>
              <div className="ht-girdi">
                <input id={`${kimlik}-dest`} value={destinasyon} maxLength={60} autoComplete="off" disabled={saltOkunur} data-testid="ht-destinasyon" onChange={(e) => setDestinasyonElle(e.target.value)} />
                {destinasyonElle !== null ? <button type="button" className="ht-baglanti" onClick={() => setDestinasyonElle(null)}>{M.destinasyonOtomatik}</button> : null}
              </div>
            </div>
            {coklu ? <div className="ht-paket-bolum">{paketAlani}</div> : null}
          </section>

          {coklu ? (
            <div className="ht-sekmeler" role="tablist" aria-label={M.bolum.sekmeler} data-testid="ht-sekmeler">
              {gorunen.map((b, i) => {
                const secili = rs[i]?.isaretli[0];
                return (
                  <button
                    key={b.anahtar}
                    type="button"
                    role="tab"
                    id={`${kimlik}-${b.anahtar}-sekme`}
                    aria-selected={b.anahtar === aktifSecili}
                    aria-controls={`${kimlik}-${b.anahtar}-panel`}
                    tabIndex={b.anahtar === aktifSecili ? 0 : -1}
                    className="ht-sekme"
                    data-testid={`ht-sekme-${i}`}
                    onClick={() => setAktifAnahtar(b.anahtar)}
                    onKeyDown={(e) => {
                      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                      e.preventDefault();
                      const hedef = gorunen[(i + (e.key === 'ArrowRight' ? 1 : gorunen.length - 1)) % gorunen.length];
                      if (hedef === undefined) return;
                      setAktifAnahtar(hedef.anahtar);
                      window.requestAnimationFrame(() => document.getElementById(`${kimlik}-${hedef.anahtar}-sekme`)?.focus());
                    }}
                  >
                    <span>{M.bolum.etiket(i + 1)}{b.giris !== '' && b.cikis !== '' ? `, ${tarihAraligiKisa(b.giris, b.cikis)}` : ''}</span>
                    <small>{secili === undefined ? M.bolum.sekmeBos : `${M.bolum.sekmeSecili}: ${secili.sonuc.otelAdi ?? secili.otel.ad}`}</small>
                  </button>
                );
              })}
            </div>
          ) : null}

          {gorunen.map((b, i) => (
            <Bolum
              key={b.anahtar}
              kimlikKok={kimlik}
              indeks={i}
              anahtar={b.anahtar}
              etiket={M.bolum.etiket(i + 1)}
              coklu={coklu}
              aktif={b.anahtar === aktifSecili}
              giris={b.giris}
              cikis={b.cikis}
              setAralik={(g, c) => aralikDegistir(b.anahtar, g, c)}
              tarihHata={tarihHatalari[i] ?? null}
              tarihBolumdeGoster={coklu}
              odalar={gorunenOdalar}
              yasTamam={yasTamam}
              odalariUygula={odalariUygula}
              musteri={musteri}
              uyruk={uyruk}
              salt={b.salt}
              yan={coklu ? null : paketAlani}
              silinebilir={coklu}
              sil={() => bolumSil(b.anahtar)}
              duyur={duyur}
              rapor={rapor}
              bitti={bolumBitti}
              baslangic={geriKurulan?.[b.anahtar] ?? null}
            />
          ))}

          <SerbestKalemAlani
            kalemler={gorunenKalemler}
            degerler={kalemDegerleri}
            secenekler={secenekListesi}
            hedefPara={ilkPara}
            kisi={toplamlar.yetiskin + toplamlar.cocuk}
            gece={coklu ? null : gunSayisi(ilkGiris, sonCikis)}
            devreDisi={saltOkunur}
            degistir={(id, yama) => setKalemler((l) => l.map((k) => (k.id === id ? { ...k, ...yama } : k)))}
            ekle={() => setKalemler((l) => [...l, yeniKalem(ilkPara ?? 'USD')])}
            kaldir={(id) => setKalemler((l) => l.filter((k) => k.id !== id))}
          />

          <PlanAlani planlama={gorunenPlanlama} isleyis={gorunenIsleyis} devreDisi={saltOkunur} planlamaDegisti={setPlanlamaMetni} isleyisDegisti={setIsleyisMetni} />

          <section className="ht-kart" data-testid="ht-teklif-notu-kutu">
            <label className="ht-ek-not">
              <span>{M.teklifNotuEtiket}</span>
              <textarea rows={3} maxLength={600} value={gorunenTeklifNotu} disabled={saltOkunur} data-testid="ht-teklif-notu" onChange={(e) => setTeklifNotuMetni(e.target.value)} />
            </label>
            <div className="ht-alan ht-kdv-alani">
              <label htmlFor={`${kimlik}-kdv`}>{M.kdv.etiket}</label>
              <input id={`${kimlik}-kdv`} value={gorunenKdv} maxLength={60} autoComplete="off" disabled={saltOkunur} data-testid="ht-kdv-notu" onChange={(e) => setKdvNotu(e.target.value)} />
              <span className="ht-ipucu">{M.kdv.ipucu}</span>
            </div>
          </section>
          <MesajBlogu
            etiket={M.mesajEtiket}
            metin={mesajMetni}
            metinDegisti={setMesajMetni}
            guncelDegil={mesajGuncelDegil}
            eskimis={mesajEskimis}
            kopyalaAcik={kapi.acik && !mesajEskimis}
            kopyalaNedeni={nedenMetni ?? (mesajEskimis ? M.neden.eskiMesaj : null)}
            pdfAcik={kapi.acik}
            ton={ton}
            tonDegisti={(t) => { setTon(t); if (kapi.acik && mesajMetni !== '') void mesajYap(t); }}
            kopyala={() => void kopyala()}
            pdfIndir={() => istek('pdf')}
            mesgul={mesgul !== null}
          />
        </div>

        <TeklifOzeti
          satirlar={coklu ? [] : tekSatirlari}
          aralik={aralik}
          odaMetni={odaMetniOzet + geceMetni}
          para={ozetPara}
          uyarilar={ozetUyarilari}
          bilgi={surer ? M.ozetKismi : null}
          kapiAcik={kapi.acik}
          neden={kapi.neden}
          nedenMetni={nedenMetni}
          mesgul={mesgul}
          pdfOlustur={() => istek('pdf')}
          pdfDili={pdfDili}
          pdfDiliDegisti={setPdfDili}
          mesajOlustur={() => istek('mesaj')}
          onayBekliyor={onayBekliyor !== null}
          onayla={() => { const t = onayBekliyor; setOnayBekliyor(null); if (t === 'pdf') void pdfYap(); else if (t === 'mesaj') void mesajYap(); }}
          vazgec={() => setOnayBekliyor(null)}
          baslik={baslikOnizleme}
          plan={plan}
          sayiMetni={coklu ? M.bolum.sayi(gorunen.length) : undefined}
        />
      </div>
      {kayitliTaslak !== null && salt === null ? <div className="ht-bilgi-kutu ht-bilgi-uyari" data-testid="ht-taslak-bildirimi"><Ikon ad="uyari" /><div><strong>{M.kayitliTaslakBaslik(gunAyKisa(new Date(kayitliTaslak.kayitMs).toISOString()), saatYaz(new Date(kayitliTaslak.kayitMs).toISOString()))}</strong><p>{M.taslakFiyatUyarisi}</p><button type="button" className="ht-btn ht-btn-p" onClick={taslakGeriYukle} data-testid="ht-taslak-geri-yukle">{M.taslakGeriYukle}</button></div></div> : null}
      <SurumSeridi surumler={surumler} aktif={salt?.no ?? null} ac={surumAc} taslagaDon={() => setSalt(null)} hata={surumHata} />
    </div>
  );
}
